import { useMemo, useRef, useState } from "react";
import { api, formatApiErrorDetail, downloadBlobResponse } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import ProdiSelect from "@/components/ProdiSelect";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Upload, FileSpreadsheet, Download, AlertCircle, CheckCircle2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { seesAllProdi, isAdminKoas, bolehImporBiodataLonggar, istilahProdi } from "@/lib/roles";

export default function ImportPesertaDialog({ open, onOpenChange, prodiList, onImported }) {
    const { user } = useAuth();
    const isSuper = seesAllProdi(user.role); // "isSuper" di sini = boleh pilih prodi bebas (super_admin / admin_ufp)
    const isKoas = isAdminKoas(user.role);
    // Admin Koas bermodul: selalu ke modulnya (server juga memaksanya) -- seperti Admin Prodi, tanpa pilihan.
    // Akun Admin Koas lama tanpa modul: WAJIB pilih dari antara prodi berjenjang Koas, spt isSuper tapi difilter.
    const needsProdiPicker = isSuper || (isKoas && !user.prodi_id);
    const istilah = istilahProdi(user.role);
    const [step, setStep] = useState(1); // 1: upload, 2: preview, 3: result
    const [chosenProdi, setChosenProdi] = useState(needsProdiPicker ? "" : user.prodi_id);
    const [file, setFile] = useState(null);
    const [parsing, setParsing] = useState(false);
    const [preview, setPreview] = useState(null);
    const [committing, setCommitting] = useState(false);
    const [result, setResult] = useState(null);
    const fileInputRef = useRef(null);
    // Opsi "izinkan biodata belum lengkap" -- khusus Super Admin, wajib konfirmasi dulu.
    const bolehLonggar = bolehImporBiodataLonggar(user.role);
    const [biodataLonggar, setBiodataLonggar] = useState(false);
    const [konfirmasiLonggar, setKonfirmasiLonggar] = useState(false);

    const prodiOptions = isKoas ? prodiList.filter((p) => p.jenjang === "Koas") : prodiList;

    const effectiveProdi = useMemo(() => {
        const id = needsProdiPicker ? chosenProdi : user.prodi_id;
        return prodiList.find((p) => p.id === id) || null;
    }, [chosenProdi, needsProdiPicker, prodiList, user.prodi_id]);

    const reset = () => {
        setStep(1); setFile(null); setPreview(null); setResult(null); setBiodataLonggar(false);
        setChosenProdi(needsProdiPicker ? "" : user.prodi_id);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const handleClose = (v) => {
        if (!v) { reset(); }
        onOpenChange(v);
    };

    const downloadTemplate = async () => {
        if (needsProdiPicker && !chosenProdi) return toast.error(`Pilih ${istilah.singkat} terlebih dahulu`);
        try {
            const params = needsProdiPicker ? { prodi_id: chosenProdi } : {};
            const res = await api.get("/peserta/import/template", { params, responseType: "blob" });
            downloadBlobResponse(res, `aura_template-import_${effectiveProdi?.nama_prodi || "prodi"}.xlsx`);
        } catch {
            toast.error("Gagal mengunduh template");
        }
    };

    const doParse = async () => {
        if (!file) return toast.error("Pilih file .xlsx terlebih dahulu");
        if (needsProdiPicker && !chosenProdi) return toast.error(`Pilih ${istilah.singkat} terlebih dahulu`);
        setParsing(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            if (needsProdiPicker) fd.append("prodi_id", chosenProdi);
            if (bolehLonggar && biodataLonggar) fd.append("izinkan_biodata_belum_lengkap", "true");
            const { data } = await api.post("/peserta/import/parse", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setPreview(data);
            setStep(2);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setParsing(false);
        }
    };

    const doCommit = async () => {
        if (!preview) return;
        const validRows = preview.rows
            .filter((r) => r.valid)
            .map((r) => ({ ...r.data, row_num: r.row_num }));
        if (validRows.length === 0) return toast.error("Tidak ada baris valid untuk disimpan");
        const invalidRows = preview.rows
            .filter((r) => !r.valid)
            .map((r) => ({
                row_num: r.row_num,
                nama: r.data.nama || "",
                npm: r.data.npm || "",
                error: r.errors.join("; "),
            }));
        setCommitting(true);
        try {
            const { data } = await api.post("/peserta/import/commit", {
                prodi_id: preview.prodi_id,
                rows: validRows,
                filename: preview.filename || file?.name || "",
                total_rows: preview.total,
                invalid_rows: invalidRows,
                // mengikuti preview dari server (bukan state kotak centang) supaya parse & commit sama
                izinkan_biodata_belum_lengkap: !!preview.biodata_longgar,
            });
            // Combine with invalid rows from preview for full report
            const invalidFromPreview = preview.rows
                .filter((r) => !r.valid)
                .map((r) => ({
                    nama: r.data.nama, npm: r.data.npm,
                    error: r.errors.join("; "),
                }));
            setResult({
                ...data,
                total_input: preview.total,
                invalid_preview: invalidFromPreview,
            });
            setStep(3);
            toast.success(`${data.inserted} baris berhasil disimpan`);
            onImported && onImported();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail, {
                rowNumFor: (idx) => validRows[idx]?.row_num,
            }));
        } finally {
            setCommitting(false);
        }
    };

    const downloadFailedLog = () => {
        if (!result) return;
        const rows = [
            ["Nama", "NPM", "Alasan Gagal"],
            ...result.invalid_preview.map((r) => [r.nama || "", r.npm || "", r.error || ""]),
            ...result.failed.map((r) => [r.nama || "", r.npm || "", r.error || ""]),
        ];
        const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
        const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `log_import_gagal_${Date.now()}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="import-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <FileSpreadsheet className="w-5 h-5 text-teal-600" />
                        Import Peserta Didik dari Excel
                    </DialogTitle>
                </DialogHeader>

                {/* Stepper */}
                <div className="flex items-center gap-2 text-xs">
                    {[
                        { n: 1, label: "Upload" },
                        { n: 2, label: "Preview & Validasi" },
                        { n: 3, label: "Hasil" },
                    ].map((s, i, arr) => (
                        <div key={s.n} className="flex items-center gap-2">
                            <div className={`w-7 h-7 rounded-full flex items-center justify-center font-bold ${step >= s.n ? "bg-[#267B84] text-white" : "bg-slate-200 text-slate-500"}`}>{s.n}</div>
                            <span className={step >= s.n ? "text-slate-800 font-semibold" : "text-slate-500"}>{s.label}</span>
                            {i < arr.length - 1 && <div className="w-8 h-px bg-slate-300" />}
                        </div>
                    ))}
                </div>

                {step === 1 && (
                    <div className="space-y-4">
                        {needsProdiPicker && (
                            <div>
                                <Label className="text-xs font-semibold uppercase text-slate-600">
                                    {istilah.singkat} Tujuan{isKoas ? " (Koas)" : ""}
                                </Label>
                                <ProdiSelect
                                    prodi={prodiOptions}
                                    value={chosenProdi}
                                    onValueChange={setChosenProdi}
                                    placeholder={isKoas ? "Pilih modul Koas tujuan import" : "Pilih prodi tujuan import"}
                                    searchPlaceholder={`Cari nama ${istilah.kecil}...`}
                                    emptyText={`${istilah.singkat} tidak ditemukan.`}
                                    testId="import-prodi-select"
                                />
                            </div>
                        )}
                        {!needsProdiPicker && effectiveProdi && (
                            <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 text-sm">
                                Data akan diimpor ke {istilah.kecil} anda: <span className="font-bold">[{effectiveProdi.jenjang}] {effectiveProdi.nama_prodi}</span>
                            </div>
                        )}

                        <div className="p-4 rounded-lg border border-teal-200 bg-teal-50">
                            <div className="flex items-start gap-3">
                                <Download className="w-5 h-5 text-teal-700 mt-0.5" />
                                <div className="flex-1">
                                    <div className="font-bold text-teal-900">Langkah 1: Unduh Template</div>
                                    <p className="text-xs text-teal-800 mt-1">
                                        Template berisi kolom header sesuai jenjang {istilah.kecil} anda. Isi mulai baris ke-5, jangan ubah nama kolom.
                                        Kolom "Tempat Lahir", "Tanggal Lahir", "No HP", dan "Alamat" wajib diisi untuk semua baris,
                                        kecuali Status Keaktifan = Lulus atau Meninggal (data historis boleh kosong).
                                    </p>
                                    <Button
                                        onClick={downloadTemplate}
                                        variant="outline"
                                        className="mt-3 border-teal-300 text-teal-800 hover:bg-teal-100"
                                        data-testid="import-download-template"
                                    >
                                        <Download className="w-4 h-4 mr-2" /> Download Template Excel
                                    </Button>
                                </div>
                            </div>
                        </div>

                        <div className="p-4 rounded-lg border border-slate-200">
                            <div className="font-bold text-slate-800 mb-1">Langkah 2: Upload File yang Sudah Diisi</div>
                            <p className="text-xs text-slate-500 mb-3">Format .xlsx. Sistem akan validasi tiap baris sebelum menyimpan.</p>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                onChange={(e) => setFile(e.target.files?.[0] || null)}
                                data-testid="import-file-input"
                                className="block w-full text-sm text-slate-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#00B9AD] file:text-[#0F2E30] hover:file:bg-[#60C0D0] cursor-pointer"
                            />
                            {file && <div className="text-xs text-slate-600 mt-2">Terpilih: <span className="font-mono">{file.name}</span> ({Math.round(file.size / 1024)} KB)</div>}
                        </div>

                        {bolehLonggar && (
                            <div className="p-4 rounded-lg border border-amber-300 bg-amber-50">
                                <label className="flex items-start gap-3 cursor-pointer">
                                    <Checkbox
                                        checked={biodataLonggar}
                                        onCheckedChange={(v) => (v ? setKonfirmasiLonggar(true) : setBiodataLonggar(false))}
                                        data-testid="import-biodata-longgar-checkbox"
                                        className="mt-0.5"
                                    />
                                    <span>
                                        <span className="font-bold text-amber-900">Izinkan biodata belum lengkap (khusus Super Admin)</span>
                                        <span className="block text-xs text-amber-800 mt-1">
                                            Tempat Lahir, Tanggal Lahir, No HP, Alamat, NIK, dan Email boleh kosong. Nama, NPM, Status,
                                            Stase, dan Tahap (Sp1) tetap wajib; isian yang diisi tetap dicek formatnya. Peserta yang
                                            biodatanya belum lengkap ditandai dan muncul di kartu "Perlu dilengkapi" agar prodi melengkapinya.
                                        </span>
                                    </span>
                                </label>
                            </div>
                        )}
                    </div>
                )}

                {step === 2 && preview && (
                    <div className="space-y-3">
                        <div className="grid grid-cols-3 gap-3">
                            <div className="p-3 rounded-lg bg-slate-100 border border-slate-200">
                                <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Total Baris</div>
                                <div className="text-2xl font-extrabold text-slate-900">{preview.total}</div>
                            </div>
                            <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                                <div className="text-[11px] uppercase tracking-widest text-emerald-700 font-semibold">Valid (siap simpan)</div>
                                <div className="text-2xl font-extrabold text-emerald-700" data-testid="import-preview-valid-count">{preview.valid}</div>
                            </div>
                            <div className="p-3 rounded-lg bg-rose-50 border border-rose-200">
                                <div className="text-[11px] uppercase tracking-widest text-rose-700 font-semibold">Error (diskip)</div>
                                <div className="text-2xl font-extrabold text-rose-700" data-testid="import-preview-invalid-count">{preview.invalid}</div>
                            </div>
                        </div>

                        <div className="text-xs text-slate-600">
                            {istilah.singkat} tujuan: <span className="font-bold">[{preview.jenjang}] {preview.prodi_nama}</span>
                        </div>
                        {preview.biodata_longgar && (
                            <div className="p-3 rounded-lg border border-amber-300 bg-amber-50 text-xs text-amber-900" data-testid="import-biodata-longgar-info">
                                Biodata belum lengkap diizinkan: <span className="font-bold">{preview.biodata_belum_lengkap}</span> baris valid
                                akan ditandai "biodata belum lengkap". Impor ini dicatat di Audit Log.
                            </div>
                        )}

                        <div className="border border-slate-200 rounded-lg overflow-hidden">
                            <div className="max-h-96 overflow-auto">
                                <table className="w-full compact">
                                    <thead className="bg-slate-100 sticky top-0">
                                        <tr className="text-left text-xs uppercase tracking-wider text-slate-600 font-semibold">
                                            <th>Status</th><th>Baris</th><th>Nama</th><th>NPM</th><th>Jenis Identitas</th><th>NIK / No. Paspor</th><th>Email</th><th>Semester</th><th>Periode Masuk</th><th>Status Peserta</th><th>Stase</th><th>Error</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {preview.rows.map((r, idx) => (
                                            <tr key={idx} className={`border-t border-slate-200 ${r.valid ? "" : "bg-rose-50/50"}`}>
                                                <td>
                                                    {r.valid
                                                        ? <span className="inline-flex items-center gap-1 text-emerald-700 text-[11px] font-bold"><CheckCircle2 className="w-3.5 h-3.5" /> VALID</span>
                                                        : <span className="inline-flex items-center gap-1 text-rose-700 text-[11px] font-bold"><AlertCircle className="w-3.5 h-3.5" /> ERROR</span>
                                                    }
                                                </td>
                                                <td className="font-mono text-xs text-slate-500">#{r.row_num}</td>
                                                <td className="font-semibold text-slate-800">{r.data.nama || <em className="text-slate-400">(kosong)</em>}</td>
                                                <td className="font-mono text-xs">{r.data.npm || "-"}</td>
                                                <td className="text-xs text-slate-600">{r.data.jenis_identitas || "NIK"}</td>
                                                <td className="font-mono text-xs">{r.data.nik || "-"}</td>
                                                <td className="text-xs text-slate-600">{r.data.email || "-"}</td>
                                                <td className="text-xs text-slate-600">{r.data.semester || "-"}</td>
                                                <td className="text-xs text-slate-600">
                                                    {r.data.periode && r.data.tahun_akademik_mulai ? `${r.data.periode} ${r.data.tahun_akademik_mulai}/${r.data.tahun_akademik_mulai + 1}` : "-"}
                                                </td>
                                                <td><Badge variant="outline" className="text-[10px]">{r.data.status_keaktifan || "-"}</Badge></td>
                                                <td><Badge variant="outline" className="text-[10px]">{r.data.stase || "-"}</Badge></td>
                                                <td className="text-xs text-rose-700">
                                                    {r.errors.length > 0 && (
                                                        <ul className="list-disc pl-4 space-y-0.5">
                                                            {r.errors.map((e, i) => <li key={i}>{e}</li>)}
                                                        </ul>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                        {preview.rows.length === 0 && (
                                            <tr><td colSpan={9} className="text-center py-8 text-slate-400">File tidak berisi baris data.</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                {step === 3 && result && (
                    <div className="space-y-4">
                        <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200">
                            <div className="flex items-start gap-3">
                                <CheckCircle2 className="w-6 h-6 text-emerald-600 mt-0.5" />
                                <div>
                                    <div className="font-extrabold text-emerald-900 text-lg">Import selesai</div>
                                    <div className="text-sm text-emerald-800 mt-1">
                                        <span className="font-bold" data-testid="import-result-inserted">{result.inserted}</span> baris berhasil disimpan
                                        {" · "}
                                        <span className="font-bold text-rose-700" data-testid="import-result-failed">{(result.invalid_preview?.length || 0) + result.failed_count}</span> baris gagal / diskip
                                        {" · "}
                                        dari total <span className="font-bold">{result.total_input}</span> baris di file.
                                    </div>
                                </div>
                            </div>
                        </div>

                        {((result.invalid_preview?.length || 0) + result.failed_count) > 0 && (
                            <div className="space-y-2">
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                    <div className="font-bold text-slate-800">Detail baris yang gagal / diskip</div>
                                    <Button variant="outline" size="sm" onClick={downloadFailedLog} data-testid="import-download-failed-log">
                                        <Download className="w-3.5 h-3.5 mr-1.5" /> Download Log (.csv)
                                    </Button>
                                </div>
                                <div className="border border-slate-200 rounded-lg overflow-hidden max-h-72 overflow-y-auto">
                                    <table className="w-full compact">
                                        <thead className="bg-slate-100 sticky top-0">
                                            <tr className="text-left text-xs uppercase tracking-wider text-slate-600 font-semibold">
                                                <th>Nama</th><th>NPM</th><th>Alasan</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {[...(result.invalid_preview || []), ...result.failed].map((r, i) => (
                                                <tr key={i} className="border-t border-slate-200">
                                                    <td className="font-semibold text-slate-800">{r.nama || "-"}</td>
                                                    <td className="font-mono text-xs">{r.npm || "-"}</td>
                                                    <td className="text-xs text-rose-700">{r.error}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <DialogFooter className="gap-2">
                    {step === 1 && (
                        <>
                            <Button variant="outline" onClick={() => handleClose(false)}>Batal</Button>
                            <Button
                                onClick={doParse}
                                disabled={!file || parsing || (needsProdiPicker && !chosenProdi)}
                                className="bg-[#00B9AD] hover:bg-[#60C0D0]"
                                data-testid="import-parse-button"
                            >
                                <Upload className="w-4 h-4 mr-2" />
                                {parsing ? "Memproses..." : "Lanjut ke Preview"}
                            </Button>
                        </>
                    )}
                    {step === 2 && (
                        <>
                            <Button variant="outline" onClick={() => setStep(1)}>
                                <ArrowLeft className="w-4 h-4 mr-2" /> Kembali
                            </Button>
                            <Button
                                onClick={doCommit}
                                disabled={committing || preview.valid === 0}
                                className="bg-emerald-600 hover:bg-emerald-700"
                                data-testid="import-commit-button"
                            >
                                {committing ? "Menyimpan..." : `Simpan Semua (${preview.valid} baris valid)`}
                            </Button>
                        </>
                    )}
                    {step === 3 && (
                        <>
                            <Button variant="outline" onClick={() => { reset(); }} data-testid="import-do-another">Import Lagi</Button>
                            <Button onClick={() => handleClose(false)} className="bg-[#00B9AD] hover:bg-[#60C0D0]" data-testid="import-close-button">Selesai</Button>
                        </>
                    )}
                </DialogFooter>
            </DialogContent>
            <AlertDialog open={konfirmasiLonggar} onOpenChange={setKonfirmasiLonggar}>
                <AlertDialogContent data-testid="import-biodata-longgar-konfirmasi">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Izinkan impor dengan biodata belum lengkap?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Peserta tanpa NIK, Email, Tempat/Tanggal Lahir, No HP, atau Alamat akan tetap masuk ke AURA dan
                            ditandai "biodata belum lengkap". Prodi perlu melengkapinya. Impor ini dicatat di Audit Log
                            atas nama Anda.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="import-biodata-longgar-batal">Batal</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => { setBiodataLonggar(true); setKonfirmasiLonggar(false); }}
                            data-testid="import-biodata-longgar-setuju"
                        >
                            Ya, izinkan
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Dialog>
    );
}
